import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { CAPErrorCode, CAPErrorInfo } from '../models/cap-error.model';
import { ErrorDialog, ErrorDialogErrorCategory, ErrorDialogErrorType } from '../models/error-dialog.model';
import { FileType } from '../models/file-item.model';
import { KnownWorkloadReferralNames } from '../models/known-workload-referral-names.model';
import { DocumentService } from '../services/document.service';
import { ErrorDialogService } from '../services/error-dialog.service';
import { SwayConfigService } from '../services/sway-config.service';
import { UrlAuthenticatorService } from '../services/url-authenticator.service';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { AppSpecificResource, LocService } from '../shared-context/loc.service';

import './error-dialog.component.css';

// TODO: Load this component lazily only when there is an ErrorDialog ready to be shown.
@Component({
    selector: 'ohp-error-dialog',
    templateUrl: './error-dialog.component.html'
})
export class ErrorDialogComponent implements OnInit, OnDestroy {
    @ViewChild('errorInput') public errorInput: ElementRef;

    public errorDialog: ErrorDialog;

    public genericActionLinkId = 'generic-action-link';

    public readonly area = KnownAreas.ErrorDialog;
    public title: string;
    public subtitle: string;
    public content: string;
    public lastFocusedElementId: string;
    public dismissButtonEnabled: boolean;
    public actionButtonTitle: string;
    public actionButtonAriaLabel: string;

    // Custom html for create errors
    public createErrorActionParts: Array<string>;
    public createActionLinkAriaLabel: string;

    // OneDrive provisioning properties
    public checkAccountStatusText: string;
    public checkAccountStatusAriaLabel: string;

    public isCreateGenericError: boolean;
    public isCreateCAPError: boolean;

    public fallbackLinkId: string;
    public showFallbackLink: boolean;
    public fallbackLinkText: string;
    public fallbackLinkAriaLabel: string;
    public fallbackSiteUrl: string;
    public fallbackSiteReferral: string;

    public correlationIdText: string | undefined;

    private dismissOnAction: boolean;
    private errorSubscription: Subscription;
    private errorDismissSubscription: Subscription;

    private readonly goToFallbackId = 'go-to-fallback-error';
    private readonly checkAccountStatusLinkId = 'check-account-status';
    private readonly goToFallbackDisabledErrorLinkId = 'go-to-fallback-disabled-error';

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly documentService: DocumentService,
        private readonly errorDialogService: ErrorDialogService,
        private readonly locService: LocService,
        private readonly contextService: ContextService,
        private readonly instrumentationService: InstrumentationService,
        private readonly swayConfigService: SwayConfigService,
        private readonly urlAuthenticatorService: UrlAuthenticatorService
    ) {}

    public ngOnInit() {
        this.errorSubscription = this.errorDialogService.error.subscribe(error => this.handleError(error));
    }

    public ngOnDestroy() {
        if (this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }

        if (this.errorDismissSubscription) {
            this.errorDismissSubscription.unsubscribe();
        }
    }

    public onDismiss(fromObservable = false) {
        if (this.errorDialog && this.errorDialog.onClose && !(fromObservable && this.errorDialog.skipOnClose)) {
            this.errorDialog.onClose();
        }

        this.errorDialog = null;
    }

    public onActionClicked(event: Event) {
        if (this.errorDialog.onActionClicked) {
            this.errorDialog.onActionClicked();

            // prevent the click from closing some other modal that may have opened before error dialog was rendered
            // Ex: Don't close the share dialog if the CAP dialog was triggered upon starting share action.
            event.stopPropagation();
        }

        if (this.dismissOnAction) {
            this.errorDialog = null;
        }
    }

    private resetDialog() {
        this.title = undefined;
        this.subtitle = undefined;
        this.content = undefined;
        this.lastFocusedElementId = undefined;
        this.dismissButtonEnabled = false;
        this.actionButtonTitle = undefined;
        this.actionButtonAriaLabel = undefined;

        this.createErrorActionParts = undefined;
        this.createActionLinkAriaLabel = undefined;

        this.checkAccountStatusText = undefined;
        this.checkAccountStatusAriaLabel = undefined;

        this.isCreateGenericError = false;
        this.isCreateCAPError = false;

        this.fallbackLinkId = undefined;
        this.showFallbackLink = false;
        this.fallbackLinkText = undefined;
        this.fallbackLinkAriaLabel = undefined;
        this.fallbackSiteUrl = undefined;
        this.fallbackSiteReferral = undefined;
        this.dismissOnAction = false;

        this.correlationIdText = undefined;
    }

    private handleError(errorDialogModel: ErrorDialog) {
        if (!this.capabilitiesService.capabilities.errorDialogEnabled) {
            return;
        }

        if (!errorDialogModel || errorDialogModel.error === ErrorDialogErrorType.None) {
            return;
        }

        this.resetDialog();

        this.errorDialog = errorDialogModel;
        this.setupFallback();

        // Dismiss if the user does an action to dismiss
        this.errorDismissSubscription = this.errorDialog.dismissDialogObservable?.subscribe(() => this.onDismiss(true));

        const app = this.errorDialog.fileType;
        const appName = this.locService.appName(app);

        const correlationId = this.errorDialog.correlationId || this.clientStartupConfigService.config.sharedClientStartupContext.correlationId;

        if (correlationId) {
            this.correlationIdText = `${this.locService.CorrelationIdLabel}${correlationId}`;
        }

        if (errorDialogModel.category === ErrorDialogErrorCategory.OpenFailed) {
            this.title = this.locService.ErrorTitleOpenLink;
            let tempSubtitle = this.locService.ErrorSubtitleOpenLink;
            switch (this.errorDialog.error) {
                case ErrorDialogErrorType.OpenNewDocFailed:
                    this.title = this.locService.appResource(AppSpecificResource.ErrorTitleOpenNew, app);
                    tempSubtitle = this.locService.appResource(AppSpecificResource.ErrorSubtitleOpenNew, app);
                    break;
            }

            if (errorDialogModel.inputText) {
                this.subtitle = tempSubtitle;
                this.dismissButtonEnabled = false;
                if (this.documentService.nativeDocument.execCommand) {
                    this.actionButtonTitle = this.locService.Copy;
                    this.actionButtonAriaLabel = this.locService.CopyLinkActionTitle;
                    errorDialogModel.onActionClicked = () => {
                        if (this.errorInput) {
                            this.errorInput.nativeElement.select();
                            this.documentService.nativeDocument.execCommand('copy');
                        }
                    };
                }
            } else {
                this.subtitle = '';
                this.dismissButtonEnabled = true;
            }
        } else {
            switch (this.errorDialog.error) {
                case ErrorDialogErrorType.OneDriveProvisioning:
                    this.dismissButtonEnabled = false;
                    this.showFallbackLink = true;
                    this.title = this.locService.ErrorTitleOneDriveProvisioning;
                    if (this.errorDialog.category === ErrorDialogErrorCategory.CreateFile) {
                        this.fallbackLinkId = this.goToFallbackId;
                        this.subtitle = this.locService.format(
                            this.locService.ErrorSubtitleOneDriveStorageProvisioning,
                            appName
                        );
                        this.fallbackLinkText = this.locService.GoToOneDrive;
                        this.fallbackLinkAriaLabel = this.locService.GoToOneDrive;
                        this.lastFocusedElementId = this.fallbackSiteUrl ? this.goToFallbackId : null;
                    } else {
                        this.fallbackLinkId = this.checkAccountStatusLinkId;
                        this.subtitle = this.locService.format(
                            this.locService.ErrorSubtitleOneDriveProvisioning,
                            appName
                        );
                        this.fallbackLinkText = this.locService.CheckAccountStatus;
                        this.fallbackLinkAriaLabel = this.locService.CheckAccountStatusAriaLabel;
                        this.lastFocusedElementId = this.fallbackSiteUrl ? this.checkAccountStatusLinkId : null;
                    }
                    break;

                case ErrorDialogErrorType.ConditionalAccessFallback:
                    this.setupCAPFallbackDialog(app, appName);
                    break;

                case ErrorDialogErrorType.ConditionalAccess:
                    if (this.capabilitiesService.capabilities.capPopupEnabled) {
                        this.setupCAPDialog(app, appName);
                    } else {
                        this.setupCAPFallbackDialog(app, appName);
                    }
                    break;

                case ErrorDialogErrorType.NoLicence:
                    this.title = this.locService.format(this.locService.ErrorTitleAppNotAvailable, appName);
                    this.subtitle = this.locService.ErrorSubtitleNoLicense;
                    this.dismissButtonEnabled = false;
                    break;

                case ErrorDialogErrorType.NoLicenceVisio:
                    this.title = this.locService.ErrorTitleNoLicenseVisio;
                    this.subtitle = this.locService.ErrorSubtitleNoLicenseVisio;
                    this.dismissButtonEnabled = false;
                    break;

                case ErrorDialogErrorType.NotFound:
                    this.title = this.locService.ErrorTitleDocumentNotFound;
                    this.subtitle = this.locService.ErrorSubtitleDocumentNotFound;
                    this.dismissButtonEnabled = true;
                    break;

                case ErrorDialogErrorType.Unauthorized:
                    this.title = this.locService.ErrorTitleUnauthorized;
                    this.subtitle = this.locService.ErrorSubtitleUnauthorized;
                    this.dismissButtonEnabled = true;
                    break;

                case ErrorDialogErrorType.Timeout:
                    this.title = this.locService.ErrorTitleTimeout;
                    this.subtitle = this.locService.ErrorSubtitleTimeout;
                    this.dismissButtonEnabled = true;
                    break;

                case ErrorDialogErrorType.Offline:
                    this.title = this.locService.ErrorTitleOffline;
                    this.subtitle = this.locService.ErrorSubtitleOffline;
                    this.dismissButtonEnabled = true;
                    break;

                case ErrorDialogErrorType.Generic:
                    this.dismissButtonEnabled = true;
                    if (this.errorDialog.category === ErrorDialogErrorCategory.CreateFile) {
                        this.isCreateGenericError = true;
                        this.title = this.locService.appResource(AppSpecificResource.ErrorTitleCreate, app);
                        this.subtitle = this.locService.appResource(AppSpecificResource.ErrorSubtitleCreate, app);
                        this.setupCreateErrorActionParts();
                        this.lastFocusedElementId = this.genericActionLinkId;
                    } else {
                        this.title = this.locService.ErrorTitleGeneric;
                        this.subtitle = this.locService.ErrorSubtitleGeneric;
                    }
                    break;

                case ErrorDialogErrorType.LoginRequired:
                    this.dismissButtonEnabled = false;
                    this.dismissOnAction = true;
                    this.title = this.locService.ErrorTitleLoginRequired;
                    this.subtitle = this.locService.ErrorSubtitleLoginRequired;
                    this.actionButtonTitle = this.locService.ErrorActionButtonTextLoginRequired;
                    this.actionButtonAriaLabel = this.locService.ErrorActionButtonAriaLabelLoginRequired;
                    break;

                case ErrorDialogErrorType.OneDriveDisabled:
                    this.dismissButtonEnabled = false;
                    this.showFallbackLink = true;
                    this.title = this.locService.appResource(AppSpecificResource.ErrorTitleCreate, app);
                    this.subtitle = this.locService.appResource(AppSpecificResource.ErrorSubtitleOneDriveDisabled, app);
                    this.fallbackLinkId = this.goToFallbackDisabledErrorLinkId;
                    this.fallbackLinkText = this.locService.GoToOneDrive;
                    this.fallbackLinkAriaLabel = this.locService.GoToOneDrive;
                    this.lastFocusedElementId = this.fallbackSiteUrl ? this.goToFallbackId : null;
                    break;

                case ErrorDialogErrorType.RenameFileFailed:
                    if (this.errorDialog.category === ErrorDialogErrorCategory.InvalidInput) {
                        this.title = this.locService.ErrorTitleInvalidFilename;
                        this.subtitle = this.locService.ErrorSubtitleInvalidFilename;
                        this.dismissButtonEnabled = true;
                    } else if (this.errorDialog.category === ErrorDialogErrorCategory.ServerSideError) {
                        this.title = this.locService.ErrorTitleRenameFailed;
                        this.subtitle = this.locService.ErrorSubtitleRenameFailed;
                        this.dismissButtonEnabled = true;
                    }
                    break;

                case ErrorDialogErrorType.FluidUnsupportedBrowser:
                    this.title = this.locService.ErrorTitleOpenFluid;
                    this.subtitle = this.locService.ErrorSubtitleOpenFluid;
                    this.dismissButtonEnabled = true;
                    break;

                case ErrorDialogErrorType.AutoBlockedForm:
                    this.title = this.locService.ErrorTitlePhishingForm;
                    this.subtitle = this.locService.AutoBlockedFormErrorMessage;
                    this.dismissButtonEnabled = true;
                    break;

                case ErrorDialogErrorType.TakenDownForm:
                    this.title = this.locService.ErrorTitlePhishingForm;
                    this.subtitle = this.locService.TakendownFormErrorMessage;
                    this.dismissButtonEnabled = true;
                    break;
                case ErrorDialogErrorType.WacDocumentBootFailed:
                    this.title = this.locService.WacDocumentErrorTitle;
                    this.subtitle = this.locService.WacDocumentErrorSubtitle;
                    if (this.errorDialog.onActionClicked) {
                        this.dismissButtonEnabled = false;
                        this.dismissOnAction = true;
                        this.actionButtonTitle = this.locService.FeedbackHeader;
                    } else  {
                        this.dismissButtonEnabled = true;
                    }
                    break;
            }
        }

        this.instrumentImpression();
    }

    private setupCAPDialog(app: FileType, appName: string) {
        const capErrorInfo = this.errorDialog.errorInfo as CAPErrorInfo;
        if (!capErrorInfo) {
            this.setupCAPFallbackDialog(app, appName);
            return;
        }

        // TODO: have more individualized messages per CAP error code.
        if (capErrorInfo.capErrorCode === CAPErrorCode.MessageOnly) {
            this.dismissButtonEnabled = false;
            this.dismissOnAction = true;

            if (this.errorDialog.category === ErrorDialogErrorCategory.CreateFile) {
                this.title = this.locService.appResource(AppSpecificResource.ErrorTitleCreate, app);
            } else {
                this.title = this.locService.ErrorTitleConditionalAccess;
            }

            this.subtitle = this.locService.format(this.locService.ErrorSubtitleCAPMessageOnly, appName);
            this.actionButtonTitle = this.locService.LearnMore;
            this.actionButtonAriaLabel = this.locService.ButtonLearnMoreCAPAriaLabel;
        } else {
            this.dismissButtonEnabled = false;
            this.dismissOnAction = true;

            this.title = this.locService.ErrorTitleCAPAdditionalStep;
            this.subtitle = this.getCAPSubtitleStringBasicAction(appName);
            this.actionButtonTitle = this.locService.ButtonNextPage;
            this.actionButtonAriaLabel = this.locService.ButtonNextCAPAriaLabel;
        }
    }

    private setupCAPFallbackDialog(app: FileType, appName: string) {
        this.errorDialog.error = ErrorDialogErrorType.ConditionalAccessFallback; // to log the correct error value in case we're falling back from popup dialog
        this.dismissButtonEnabled = true;
        this.subtitle = this.locService.format(this.locService.ErrorSubtitleConditionalAccess, appName);
        if (this.errorDialog.category === ErrorDialogErrorCategory.CreateFile) {
            this.isCreateCAPError = true;
            this.title = this.locService.appResource(AppSpecificResource.ErrorTitleCreate, app);
            this.setupCreateErrorActionParts();
        } else {
            this.title = this.locService.ErrorTitleConditionalAccess;
        }
    }

    private getCAPSubtitleStringBasicAction(app: string): string {
        switch (this.errorDialog.category) {
            case ErrorDialogErrorCategory.Share:
                return this.locService.ErrorSubtitleCAPShareAdditionalStep;
            case ErrorDialogErrorCategory.Upload:
                return this.locService.ErrorSubtitleCAPUploadAdditionalStep;
            default:
                return this.locService.format(this.locService.ErrorSubtitleCAPAdditionalStep, app);
        }
    }

    private setupFallback() {
        if (this.errorDialog.category === ErrorDialogErrorCategory.CreateFile) {
            if (this.errorDialog.fileType === FileType.Sway) {
                this.fallbackSiteUrl = this.urlAuthenticatorService.appendPvrAndUpnAuthParams(
                    this.swayConfigService.config.myEndpoint
                );
                this.fallbackSiteReferral = KnownWorkloadReferralNames.Sway;
            } else {
                this.fallbackSiteUrl = this.contextService.sharedContext.oneDriveUrl;
                this.fallbackSiteReferral = KnownWorkloadReferralNames.OneDrive;
            }
        } else if (this.errorDialog.error === ErrorDialogErrorType.OneDriveProvisioning) {
            this.fallbackSiteUrl = this.contextService.sharedContext.oneDriveUrl;
            this.fallbackSiteReferral = KnownWorkloadReferralNames.OneDrive;
        }
    }

    private setupCreateErrorActionParts() {
        const text = this.isCreateCAPError
            ? this.locService.appResource(AppSpecificResource.CreateCAPErrorActionText, this.errorDialog.fileType)
            : this.locService.appResource(AppSpecificResource.CreateErrorActionText, this.errorDialog.fileType);
        if (!text) {
            return;
        }
        this.createActionLinkAriaLabel = text.replace('<c0>', '').replace('</c0>', '');

        let parts = text.split('<c0>');
        if (parts.length !== 2) {
            this.createErrorActionParts = [text];
            return;
        }

        const beforeLinkText = parts[0];
        const rest = parts[1];
        parts = rest.split('</c0>');

        if (parts.length < 2) {
            this.createErrorActionParts = [text];
            return;
        }

        const linkText = parts[0];
        const afterLinkText = parts[1];

        // TODO need to validate this order works with final translated strings.
        this.createErrorActionParts = this.contextService.isRtl
            ? [afterLinkText, linkText, beforeLinkText]
            : [beforeLinkText, linkText, afterLinkText];
    }

    private instrumentImpression() {
        if (!this.errorDialog) {
            return;
        }

        const params: InstrumentationParams = {};
        params[InstrumentationParam.ItemCategory] = this.errorDialog.category;
        params[InstrumentationParam.Error] = this.errorDialog.error;
        if (this.errorDialog.fileType) {
            params[InstrumentationParam.FileFormat] = FileType[this.errorDialog.fileType];
        }
        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.ErrorDialogImpression, params);
    }
}
